import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/ri-edit-line.svg';
import { ReactComponent as AlertIcon } from '@/assets/icons/ri-alert-line.svg';
import Button from '@/components/common/Button';
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import Modal from '@/components/common/Modal';
import Table from '@/components/common/Table';
import { useAppDispatch, RootState } from '@/store';
import { STATUS, STATUS_FILTER_COLOR_MAPPING } from '@/pages/Customers/constant';
import { getCustomerList, createCustomer, editCustomer, getCustomerSummary } from '@/store/customer/action';
import { renderCustomerFormFooter, renderCustomerFormBody, renderCustomerFormHeader } from '@/pages/Customers/Modal';
import useCustomPaging from '@/hooks/useCustomPaging';
import useSearchFilter from '@/hooks/useSearchFilter';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';

const renderCustomerPageHeader = (
  searchString: string,
  handleChangeSearchString: any,
  handleSubmitSearch: any,
  showModal: Function
) => (
  <BreadcrumbLayout>
    <div className="flex justify-between items-center gap-8 p-0">
      <p className="font-bold text-2xl text-black-primary">Customers</p>
      <div className="flex items-center gap-4">
        <input
          value={searchString}
          onChange={handleChangeSearchString}
          onKeyDown={handleSubmitSearch}
          name="searchString"
          type="text"
          className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
          placeholder="Search for Customers"
        />
        <div className="bg-grey-line-subtle h-6 w-1"/>
        <Button type="submit" variant="primary" className="text-white" onClick={() => showModal()}>
          <AddIcon/>
          <p>Customer</p>
        </Button>
      </div>
    </div>
  </BreadcrumbLayout>
)

const renderCustomerPageFilterSection = (selectFilter: Function, customerMeta, customerSummary) => (
  <div className="flex justify-between items-center p-4 p-6 bg-white border border-grey-line-subtle border-b-0 rounded-t">
    <div className="flex items-center p-0 gap-4">
      {<div className='flex items-start'>
        {customerSummary && STATUS(customerSummary).map(status => {
          const btnClass = status.key === customerMeta.status ? STATUS_FILTER_COLOR_MAPPING.ACTIVE : STATUS_FILTER_COLOR_MAPPING.INACTIVE;
          return (
            <Button type='button'
                    variant='primary'
                    key={status.key}
                    className={
                      `border
											 border-grey-line-subtle
											 rounded-none 
											 gap-3
											 ${btnClass.button}`}
                    onClick={() => selectFilter({ status: status.key })}>
              {status.label}
              <div
                className={`flex py-0 px-2 gap-3 rounded-full ${btnClass.count}`}>
                {/* TODO: Get this from API maybe */}
                {status.count}
              </div>
            </Button>);
        })}
      </div>}
      <div>
        <label htmlFor='overdueInvoices' className="flex gap-1">
          <input type='checkbox' id="overdueInvoices" onChange={(e) => selectFilter({ invoiceOverdue: `${e.target.checked}` })
          }/>
          <p>Customers with overdue invoices</p>
        </label>
      </div>
    </div>
    <div className="flex items-center p-0 gap-2">
      {/* TODO: To dropdown and do actual pagination */}
      10 Item Per Page
    </div>
  </div>
)

const renderCustomerNameTable = ({ name, is_invoice_overdue, is_blocked }) => (
  <div className="flex items-center h-full font-bold text-sm">
    <div className='flex-grow'>{name}</div>
    {(is_invoice_overdue || is_blocked) && 
      <p className="group text-red-primary"  data-tooltip-target="tooltip-default">
        <AlertIcon/>
        <div className="absolute text-xs hidden group-hover:block rounded text-red-primary bg-red-primary bg-opacity-10 p-1">
          { is_invoice_overdue && 'Invoice Overdue'}
          { (is_invoice_overdue && is_blocked) && ', '}
          { is_blocked && 'Blocked By Admin'}
        </div>
      </p>
    }
  </div>
)

export const renderCustomerStatusTable = (status: string) => {
  const bgColor = status === 'ACTIVE' ? 'bg-green-subtle' : 'bg-red-subtle'
  const textColor = status === 'ACTIVE' ? 'text-green-success' : 'text-red-secondary'

  return (
    <div className="flex justify-center p-0">
      <div className={`flex justify-center items-center px-4 py-3 gap-3 rounded-sm ${bgColor}`}>
        <p className={`font-medium text-sm ${textColor}`}>{status}</p>
      </div>
    </div>
  )
}

const HEADERS: TableHeader[] = [
  {
    label: 'Name',
    key: 'name',
  },
  {
    label: 'No. HP',
    key: 'phoneNumber',
  },
  {
    label: 'Email',
    key: 'email',
  },
  {
    label: 'Status',
    key: 'status'
  },
  {
    label: 'Action',
    key: 'action',
  }
]
const constructTableData = (data, showModal) => {
  const tableBody = data.map(customer => {
    const {
      id,
      name,
      email,
      phone_number: phoneNumber,
      status,
      is_invoice_overdue,
      club_name,
      is_blocked,
      blocked_note,
    } = customer
    return {
      id,
      value:
        {
          name: {
            name,
            is_invoice_overdue,
            is_blocked,
            club_name
          },
          phoneNumber,
          email,
          status,
          action: id,
          isBlocked: is_blocked,
          blockedNote: blocked_note,
          isInvoiceOverdue: is_invoice_overdue
        }
    }
  })
  const component = {
    name: (v) => renderCustomerNameTable(v),
    phoneNumber: (v) => (<div className="flex items-center h-full text-sm">{v}</div>),
    email: (v) => (<div className="flex items-center h-full text-sm">{v}</div>),
    status: (v) => renderCustomerStatusTable(v),
    action: (v: string) => (<div>
      <Button
        className="border border-grey-line-subtle"
        type='button'
        variant='basic'
        onClick={() => {
          showModal(v);
        }}>
        <EditIcon/>
        Edit
      </Button>
    </div>),
  }
  return {
    data: tableBody,
    component
  }
}
const SORT: TableSort = {
  type: 'SERVER',
  handler: () => {},
};


const Customers = () => {
  const customerMethods = useForm()
  const customerState = useSelector((state: RootState) => state.customer.state.items)
  const paging = useSelector((state: RootState) => state.customer.state.paging)
  const [show, setShow] = useState(false)
  const [customerSummary, setCustomerSummary] = useState({})
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
  const [
    searchString,
    currentSearchFilter,
    handleChangeSearchString,
    handleSubmitSearch
  ] = useSearchFilter()

  const customerMeta: CustomerFilter = {
    status: searchParams.get('status'),
    search: searchParams.get('search'),
    invoiceOverdue: searchParams.get('isInvoiceOverdue')
  }

  const handleCustomerMeta = ({ status, search, invoiceOverdue }: CustomerFilter) => {
    if (status || status === '') searchParams.set('status', status)
    if (search || search === '') searchParams.set('search', search)
    if (invoiceOverdue) searchParams.set('isInvoiceOverdue', `${invoiceOverdue}`)
    setSearchParams(searchParams)
  }

  const setSortingParam = (key, order) => order === 'ASC' ? key : `-${key}`

  const getAllCustomers = async () => {
    let sortBy = searchParams.get('sortBy');
    let order = searchParams.get('order');
    if (sortBy === null) sortBy = "createdAt";
    if (order === null) order = "DESC";
    const params = {
      sizeMeta: 10,
      ...dynamicAttr('search', currentSearchFilter),
      ...dynamicAttr('status', customerMeta.status),
      ...dynamicAttr('invoiceOverdue', customerMeta.invoiceOverdue),
      ...dynamicAttr('pageMeta', currentPageFilter),
      ...dynamicAttr('sortMeta', setSortingParam(sortBy, order)),
    }
    dispatch(getCustomerList(params))
    const summaryTypeParams = {
      ...dynamicAttr('search', currentSearchFilter),
      ...dynamicAttr('invoiceOverdue', customerMeta.invoiceOverdue)
    }
    const summary = await dispatch(getCustomerSummary(summaryTypeParams))
    setCustomerSummary(summary?.payload)
  }

  const showModal = (v?: string) => {
    if (v) {
      setSelectedCustomerId(v)
    }
    setShow(true)
  }
  const closeModal = () => {
    setSelectedCustomerId('')
    setShow(false)
  }
  const submitModal = async () => {
    const customerData = customerMethods.getValues()
    const data = {
      "name": customerData.name,
      "phone_number": customerData.phoneNumber,
      "email": customerData.email,
      "club_name": customerData.clubName,
      "status": customerData.status,
      "is_invoice_overdue": customerData.isInvoiceOverdue,
      "is_blocked": customerData.isBlocked,
      "blocked_note": customerData.blockedNote
    }
    if (!selectedCustomerId) {
      await dispatch(createCustomer(data))
    }
    else {
      await dispatch(editCustomer({data, id: selectedCustomerId}))
    }
    await getAllCustomers()
    closeModal()
  }

  const customerData = (id: string) => constructTableData(customerState, () => {}).data.find(row => row.value.action === id).value

  useEffect(() => {
    (async () => {
      await getAllCustomers()
    })()
  }, [
    customerMeta.status,
    currentSearchFilter,
    currentPageFilter,
    customerMeta.invoiceOverdue,
    searchParams.get('sortBy'),
    searchParams.get('order')
  ])

  useEffect(() => {
    if (selectedCustomerId) {
      const customer = customerData(selectedCustomerId)
      const initialCustomer = {
        name: customer.name.name,
        phoneNumber: customer.phoneNumber,
        email: customer.email,
        clubName: customer.name.club_name,
        status: customer.status,
        isInvoiceOverdue: customer.isInvoiceOverdue,
        isBlocked: customer.isBlocked,
        blockedNote: customer.blockedNote,
      }
      customerMethods.reset({...initialCustomer})
    } else { customerMethods.reset({}) }
  }, [customerMethods.reset, selectedCustomerId])

  return (
    <div className='flex flex-col w-full'>
      {renderCustomerPageHeader(
        searchString,
        handleChangeSearchString,
        handleSubmitSearch,
        showModal
      )}
      <div className='flex flex-col p-4 w-full'>
        {renderCustomerPageFilterSection(handleCustomerMeta, customerMeta, customerSummary)}
        <Table headers={HEADERS}
               body={constructTableData(customerState, showModal)}
               sort={SORT}
               meta={constructTableMetaFromPaging2(paging)}
               pageNumberHandler={handlePageNumberChange}/>
      </div>
      { show && <Modal show={show}
											 header={renderCustomerFormHeader}
											 body={renderCustomerFormBody({ customerMethods }, selectedCustomerId, () => submitModal)}
											 footer={renderCustomerFormFooter(() => closeModal, () => submitModal)}
											 onClick={() => closeModal}
											 rootId="portal-customer-form"/> }
    </div>
  );
}

export default Customers
